import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import Loader from "../components/Loader";
import Breadcrumbs from "../components/Breadcrumbs";
import api from "../api";
import "../assets/buttons.css";
import "../assets/panels.css";

/**
 * MonthlyInspectionReportView Component
 * Displays monthly inspection summary report for clients
 * Matches Laravel route: /clients/{client}/reports/monthly_inspections/{monthlyInspectionSummaryReport}
 */
const MonthlyInspectionReportView = () => {
  const { clientId, reportId } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Check if user is CLIENT
  const isClient = user?.role === "CLIENT";
  const canManageReports = hasPermission("properties.reports.monthly_inspection");

  useEffect(() => {
    loadReport();
  }, [clientId, reportId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await api.get(`/clients/${clientId}/reports/monthly-inspections/${reportId}`);
      const data = response.data;

      if (data.success) {
        setReport(data.report);
      } else {
        setError(data.message || "Failed to load report");
      }
    } catch (err) {
      console.error("Error loading report:", err);
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!window.confirm("Are you sure you want to delete this report?")) {
      return;
    }

    try {
      const response = await api.delete(`/clients/${clientId}/reports/monthly-inspections/${reportId}`);
      if (response.data.success) {
        toast.success("Report deleted successfully");
        navigate("/dashboard");
      } else {
        toast.error(response.data.message || "Failed to delete report");
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-danger">{error}</div>
        <Link to="/dashboard" className="btn btn-default">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="container">
        <div className="alert alert-warning">Report not found</div>
        <Link to="/dashboard" className="btn btn-default">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Dashboard", link: "/dashboard" },
    { label: "Reports", link: "#" },
    {
      label: `${report.months} (${report.year})`,
      link: "",
    },
  ];

  return (
    <div className="container">
      <Breadcrumbs items={breadcrumbs} />

      {/* Report Header */}
      <div className="row">
        <div className="col-md-12">
          <div className="panel-title title-standard">
            <h3 className="mr-auto">
              Monthly Inspection Report - {report.months} ({report.year})
            </h3>
            {canManageReports && !isClient && (
              <button onClick={handleDeleteReport} className="btn btn-danger ml-2">
                <i className="fa fa-trash"></i> Delete Report
              </button>
            )}
          </div>
          <div className="panel-group border-down margin-down">
            <dl className="list green-list green-list-with-width">
              <dt>Report Period:</dt>
              <dd>
                {report.months} {report.year}
              </dd>
              <dt>Status:</dt>
              <dd>
                {report.published ? (
                  <span className="label label-success">Published</span>
                ) : (
                  <span className="label label-warning">Draft</span>
                )}
              </dd>
              <dt>Last Updated:</dt>
              <dd>{new Date(report.updated_at).toLocaleDateString("en-GB")}</dd>
            </dl>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4>Report Preview</h4>
            </div>
            <div className="panel-body">
              <div className="alert alert-info">
                <i className="fa fa-info-circle"></i> PDF generation feature coming soon.
                For now, this displays the report metadata.
              </div>

              <p>
                <strong>Report ID:</strong> {report.id}
              </p>
              <p>
                <strong>Client ID:</strong> {report.client_id}
              </p>
              <p>
                <strong>Created:</strong> {new Date(report.created_at).toLocaleString("en-GB")}
              </p>

              {/* Placeholder for PDF viewer */}
              <div
                style={{
                  border: "1px solid #ddd",
                  padding: "40px",
                  textAlign: "center",
                  backgroundColor: "#f9f9f9",
                  minHeight: "400px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>
                  <i className="fa fa-file-pdf-o" style={{ fontSize: "80px", color: "#ccc" }}></i>
                  <p style={{ marginTop: "20px", color: "#666" }}>
                    PDF Report for {report.months} ({report.year})
                  </p>
                  <p style={{ color: "#999", fontSize: "14px" }}>
                    PDF generation will be implemented in future release
                  </p>
                </div>
              </div>
            </div>
            <div className="panel-footer">
              <Link to="/dashboard" className="btn btn-default">
                <i className="fa fa-arrow-left"></i> Back to Dashboard
              </Link>
              {/* Future: Download PDF button */}
              {/* <button className="btn btn-primary ml-10">
                <i className="fa fa-download"></i> Download PDF
              </button> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyInspectionReportView;
